import {
  ISSUE_IMPACT_LENGTH_MAX,
  IssueActionProgressEnum,
  IssueStatusEnum,
  MANAGEMENT_RESPONSE_LENGTH_MAX,
  RECOMMENDATIONS_LENGTH_MAX,
  REMEDIATION_LENGTH_MAX,
  ROOT_CAUSE_LENGTH_MAX,
} from '@issuemanager/constants';
import { z } from 'zod';
import {
  GetManageIssueFormSchemaArgs,
  actionApproverValidation,
  actionDescriptionValidation,
  actionDueDateValidation,
  actionDueDateValidations,
  actionOwnerValidation,
  actionSequentialDueDateValidation,
  actionTitleValidations,
  actionsAtLeastOneValidation,
  baseSchema,
  issueDueDateEarlierThanSLAPastValidation,
} from './common';

export function getManageIssueFormSubmitSchema({
  t,
  timezoneOffset,
  SLAs,
  isDateEarlierThanSLA,
  issueStatus,
  issueActionProgress,
  isDraftIssue,
}: GetManageIssueFormSchemaArgs) {
  const isNotYetInitiated =
    issueStatus === IssueStatusEnum.IN_PROGRESS &&
    (issueActionProgress === IssueActionProgressEnum.NOT_INITIATED ||
      issueActionProgress === IssueActionProgressEnum.BLANK);
  /**
   * fields are required except actions at first.
   * then according to the issue-status action's fields are required.
   * the validations that depends on issue-status | issue-progress will be done
   * in superRefine section
   */
  return (
    baseSchema
      .extend({
        title: baseSchema.shape.title.optional(),
        description: baseSchema.shape.description.optional(),
        ownerId: isDraftIssue
          ? baseSchema.shape.ownerId.nullable().optional()
          : baseSchema.shape.ownerId.refine((val) => !!val, {
              message: t('issue:form.errorMessages.owner.required'),
            }),
        severity: isDraftIssue
          ? baseSchema.shape.severity.nullable().optional()
          : baseSchema.shape.severity.refine((val) => !!val, {
              message: t('issue:form.errorMessages.severity.required'),
            }),
        dueDate: isDraftIssue
          ? baseSchema.shape.dueDate.optional()
          : baseSchema.shape.dueDate.refine((val) => !!val, {
              message: t('issue:form.errorMessages.dueDate.required'),
            }),
        managerId: isDraftIssue
          ? baseSchema.shape.managerId.nullable().optional()
          : baseSchema.shape.managerId.refine((val) => !!val, {
              message: t('issue:form.errorMessages.manager.required'),
            }),
        approverId: isDraftIssue
          ? baseSchema.shape.approverId.nullable().optional()
          : baseSchema.shape.approverId.refine((val) => !!val, {
              message: t('issue:form.errorMessages.approver.required'),
            }),
      })
      /**
       * these validations required using reference to other fields and outside data
       * validation logic is explicitly defined here to make it more readable
       * every error needs to be push individually to the context
       * the first part checks the Assign validations
       * the second part checks the Workflow validations
       */
      .superRefine((data, ctx) => {
        if (!data.title) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['title'],
            message: t('issue:form.errorMessages.title.required'),
          });
        }

        if (!data.description) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['description'],
            message: t('issue:form.errorMessages.description.required'),
          });
        }

        if (data.impact && data.impact.length > ISSUE_IMPACT_LENGTH_MAX) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['impact'],
            message: t('issue:form.errorMessages.impact.maxLength'),
          });
        }

        if (data.rootCause && data.rootCause.length > ROOT_CAUSE_LENGTH_MAX) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['rootCause'],
            message: t('issue:form.errorMessages.rootCause.maxLength'),
          });
        }

        if (
          data.recommendations &&
          data.recommendations.length > RECOMMENDATIONS_LENGTH_MAX
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['recommendations'],
            message: t('issue:form.errorMessages.recommendations.maxLength'),
          });
        }

        if (
          data.managementResponse &&
          data.managementResponse.length > MANAGEMENT_RESPONSE_LENGTH_MAX
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['managementResponse'],
            message: t('issue:form.errorMessages.managementResponse.maxLength'),
          });
        }

        if (
          data.remediation &&
          data.remediation.length > REMEDIATION_LENGTH_MAX
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['remediation'],
            message: t('issue:form.errorMessages.remediation.maxLength'),
          });
        }

        // Assignment validations

        // Issue due date SLA validation
        if (!isDraftIssue && data.dueDate && data.severity) {
          issueDueDateEarlierThanSLAPastValidation({
            ctx,
            SLAs,
            isDateEarlierThanSLA,
            t,
            data: {
              dueDate: data.dueDate,
              severity: data.severity,
            },
          });
        }

        // Workflow validations
        if (!isDraftIssue && isNotYetInitiated) {
          actionsAtLeastOneValidation({
            actions: data.actions,
            ctx,
            t,
            isDraftIssue,
          });

          data.actions?.forEach((action, index) => {
            actionDescriptionValidation({
              data: {
                description: action.description,
                index,
              },
              ctx,
              t,
            });
            actionDueDateValidation({
              data: {
                dueDate: action.dueDate,
                index,
              },
              ctx,
              t,
            });
            actionOwnerValidation({
              data: {
                ownerId: action.ownerId,
                index,
              },
              ctx,
              t,
            });

            actionApproverValidation({
              data: {
                approverId: action.approverId,
                status: action.status,
                index,
              },
              ctx,
              t,
            });
          });
        }

        // [title | unique title | due date past | due date before issue |
        // due date sequential] validations always happen
        const standardizedTitles = new Set<string>();
        data.actions?.forEach((action, index) => {
          actionTitleValidations({
            data: {
              action,
              actions: data.actions || [],
              index,
              standardizedTitles,
            },
            isDateEarlierThanSLA,
            SLAs,
            ctx,
            t,
          });
          actionDueDateValidations({
            data: {
              action,
              dueDate: data.dueDate,
              index,
            },
            timezoneOffset,
            SLAs,
            ctx,
            t,
            skipPastDateValidation: true,
          });
          actionSequentialDueDateValidation({
            data: {
              action,
              actions: data.actions || [],
              dueDate: action.dueDate,
              index,
              executionType: data.executionType,
            },
            ctx,
            t,
          });
        });
      })
  );
}

export type ManageIssueFormSubmitSchemaType = ReturnType<
  typeof getManageIssueFormSubmitSchema
>;
